import serializeForm from 'form-serialize';
import { checkRelation } from '@legacynotes/utils/lib/validation';
import { expand, collapse } from '../expandable/expandable-transition';

function initFieldRelations(element, options) {
	const form = element.nodeName === 'FORM' ? element : element.closest('form');
	const settings = Object.assign(
		{
			form,
			_csrf: form ? (form.querySelector('[name="_csrf"]') || {}).value : null
		},
		options
	);
	const dependentRows = element.querySelectorAll('[data-form-relation]');

	// Create map of available fields to minimze DOM lookups
	const availableFields = element.querySelectorAll('[name]');
	const availableFieldsMap = [].slice.call(availableFields).reduce((acc, field) => {
		const name = field.getAttribute('name');

		acc[name] = acc[name] || [];
		acc[name].push(field);

		return acc;
	}, {});

	dependentRows.forEach(row => {
		let relations = JSON.parse(row.dataset.formRelation);

		if (!Array.isArray(relations)) {
			relations = [relations];
		}

		relations = relations
			.map(relation => {
				const fields = availableFieldsMap[relation.key] || [];

				return {
					fields,
					key: relation.key,
					value: relation.value,
					skipReset: relation.skipReset,
					any: relation.any,
					async: relation.async
				};
			})
			.filter(relation => relation.fields.length);

		if (relations.length) {
			toggleRow(
				row,
				relations,
				Object.assign(
					{
						skipAnimation: true
						// skipReset: true
					},
					settings
				)
			);

			relations.forEach(relation => {
				relation.fields.forEach(field => {
					field.addEventListener('change', () => {
						setTimeout(() => {
							toggleRow(row, relations, settings);
						});
					});
				});
			});
		}
	});
}

function toggleRow(row, relations, options) {
	let skipReset = options.skipReset;
	const formData = serializeForm(options.form, {
		hash: true
	});
	const show = relations
		.map(relation => {
			const currentValue = formData[relation.key] || '';
			const referenceValue = relation.value;

			// Custom logic for calculator relations
			if (relation.key === 'calculator') {
				const configurator = JSON.parse(currentValue || '{}').configurator || [];
				const spouse = configurator.find(item => item.key === 'spouse');
				const hasSpouse = !!(spouse && spouse.isChecked);
				const firstParentela = configurator.find(item => item.key === 'firstParentela');
				const hasFirstParentela = !!(firstParentela && firstParentela.isChecked);

				switch (referenceValue) {
					case 'spouse':
						return hasSpouse;
					case 'family':
						return !hasSpouse && hasFirstParentela;
				}
			}

			const field = document.querySelector('[name="' + relation.key + '"]');
			const rowIsVisible = !!field.offsetParent;
			const result = rowIsVisible && checkRelation(currentValue, referenceValue);

			if (relation.skipReset) {
				skipReset = true;
			}

			return result;
		})
		.filter(result => result);

	if (show.length === relations.length || (relations[0].any && show.length)) {
		if (!row.offsetParent && !skipReset) {
			resetRow(row, relations, options);
		}

		showRow(row, relations, options);
	} else {
		if (!row.offsetParent && !options.skipAnimation) {
			return;
		}

		if (!skipReset) {
			resetRow(
				row,
				relations,
				Object.assign(
					{
						isHiding: true
					},
					options
				)
			);
		}

		hideRow(row, relations, options);
	}
}

function resetRow(row, relations, options) {
	const fields = getFields(row);

	if (row.dataset.formRelationPersist) {
		return;
	}

	fields.forEach(field => {
		const defaultValue = field.dataset.formFieldDefault;
		let value = null;
		let selectedIndex = 0;
		let checked = false;

		if (defaultValue && !options.isHiding) {
			const options = [].filter.call(
				field.querySelectorAll('option'),
				option => option.value === defaultValue
			);

			value = defaultValue;
			selectedIndex = options.length ? options[0].index : selectedIndex;
			checked = field.value === defaultValue;
		}

		if (['checkbox', 'radio'].includes(field.type)) {
			field.checked = checked;
		} else {
			field.value = value;
			field.selectedIndex = selectedIndex;

			// Remove hidden files
			if (field.type === 'file') {
				const remove = document.querySelector(`[name="_remove_${field.name}"]`);

				if (remove) {
					remove.checked = true;
				}
			}
		}
	});

	triggerChange(row);
}

async function showRow(row, relations, options) {
	const async = (relations.find(relation => relation.async) || {}).async;

	if (async && !row.dataset.formRelationAsyncLoaded) {
		const response = await fetch(async.url, {
			method: 'POST',
			credentials: 'same-origin',
			body: JSON.stringify({
				fieldsConfig: async.fieldsConfig,
				emptyFieldsOnly: async.emptyFieldsOnly,
				_csrf: options._csrf
			}),
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'XMLHttpRequest'
			}
		});
		const form = await response.text();

		row.innerHTML = form;
		row.dataset.formRelationAsyncLoaded = true;

		options.initForm(row);
	}

	if (row.offsetParent) {
		return;
	}

	animate(row, true, {
		skipAnimation: options.skipAnimation,
		complete: () => {
			// Add required attribute to initially required elements
			row.querySelectorAll('[data-required]').forEach(field => {
				if (field.offsetParent) {
					field.setAttribute('required', '');
					field.removeAttribute('data-required');
				}
			});

			triggerChange(row);

			if (options.onShow) {
				options.onShow(row);
			}
		}
	});

	row.dispatchEvent(
		new CustomEvent('LN:updateVisibility', {
			bubbles: true,
			detail: {
				isVisible: true
			}
		})
	);
}

function hideRow(row, relations, options) {
	animate(row, false, {
		skipAnimation: options.skipAnimation,
		complete: () => {
			// Remove required attribute from hidden elements
			row.querySelectorAll('[required]').forEach(field => {
				if (!field.offsetParent) {
					field.setAttribute('data-required', true);
					field.removeAttribute('required');
				}
			});
		}
	});

	row.dispatchEvent(
		new CustomEvent('LN:updateVisibility', {
			bubbles: true,
			detail: {
				isVisible: false
			}
		})
	);
}

function animate(row, show, options) {
	const skipAnimation = options.skipAnimation || row.dataset.formStepsTitle;

	if (show) {
		expand(row, {
			skipAnimation
		}).then(() => {
			options.complete && options.complete();
		});
	} else {
		collapse(row, {
			skipAnimation
		}).then(() => {
			options.complete && options.complete();
		});
	}
}

function getFields(row) {
	const fields = row.querySelectorAll('input, textarea, select');

	return fields;
}

function triggerChange(row) {
	const fields = getFields(row);

	fields.forEach(field => {
		field.dispatchEvent(
			new CustomEvent('change', {
				bubbles: true
			})
		);
	});
}

export { initFieldRelations };
